import { assignInlineVars } from '@vanilla-extract/dynamic';
import { getPixelValue } from '../../../../utils/units';
import { Flex } from '../../../Flex/Flex';
import { CalendarView } from '../types';
import type { Month, SideBySideView, Year } from '../types';
import type { CalendarSelectProps } from './CalendarSelect';
import { CalendarSelect } from './CalendarSelect';
import * as styles from './CalendarViews.css';

interface MonthsYearsViewProps {
  readonly months: Month[];
  readonly onMonthChange: (month: number) => void;
  readonly isMonthSelected: (month: Month) => boolean;
  readonly years: Year[];
  readonly onYearChange: (index: number) => void;
  readonly isYearSelected: (year: Year) => boolean;
  readonly calendarView: CalendarView;
  readonly isSideBySideView?: SideBySideView;
}

export const MonthsYearsView: React.FC<MonthsYearsViewProps> = ({
  months,
  onMonthChange,
  isMonthSelected,
  onYearChange,
  years,
  isYearSelected,
  calendarView,
  isSideBySideView,
}) => {
  const containerGap = getPixelValue(styles.YEAR_MONTH_CONTAINER_GAP);
  const isYearsView = calendarView === CalendarView.Year;
  const shouldShowSideBySideView = typeof isSideBySideView === 'boolean' ? isSideBySideView : isSideBySideView != null;

  const monthCalendarSelectProps = {
    items: months,
    onItemChange: onMonthChange,
    isItemSelected: isMonthSelected,
    selectedIndex: months.findIndex(isMonthSelected),
  };

  const yearCalendarSelectProps = {
    items: years,
    onItemChange: onYearChange,
    isItemSelected: isYearSelected,
    selectedIndex: years.findIndex(isYearSelected),
    numberOfItemsInRow: isSideBySideView == null ? styles.NUMBER_OF_YEARS_IN_ROW : undefined,
  };

  const commonCalendarSelectProps = {
    itemHeight: styles.ITEM_HEIGHT_NUMBER,
    isSideBySideView: isSideBySideView != null,
    containerGap: isSideBySideView == null ? containerGap : undefined,
    selectedClassName: isSideBySideView == null ? styles.selectedDate : '',
    containerClassName: isSideBySideView == null ? styles.monthsYearsWrapper : styles.sideBySideItemsWrapper,
    className: isSideBySideView != null ? styles.sideBySideItem : isYearsView ? styles.calendarItem : styles.monthItem,
  };

  if (shouldShowSideBySideView) {
    const numberOfVisibleItems =
      typeof isSideBySideView === 'object' ? isSideBySideView.numberOfVisibleItems : styles.NUMBER_OF_VISIBLE_ITEMS;

    return (
      <Flex
        className={styles.sideBySideCurrentSelectedRow}
        style={assignInlineVars({
          [styles.numberOfVisibleItemsVar]: `${numberOfVisibleItems}`,
        })}
      >
        <CalendarSelect {...monthCalendarSelectProps} {...commonCalendarSelectProps} />
        <CalendarSelect {...yearCalendarSelectProps} {...commonCalendarSelectProps} />
      </Flex>
    );
  }

  return (
    <CalendarSelect
      {...(isYearsView ? yearCalendarSelectProps : monthCalendarSelectProps)}
      {...(commonCalendarSelectProps as CalendarSelectProps<Month | Year>)}
    />
  );
};
